/**
 * The one download link in the portal, and the one reason it opens a tab of its own.
 *
 * Beside `fileType.ts` because it is the same subject and the same one import path —
 * `pages/studio/clientDetail/FileDialog.tsx` reads both from here, exactly as it reads
 * `extensionOf`. Neither belongs to the client portal any more than to the studio; a
 * later session that grows a home for shared page pieces should take them both.
 *
 * ## It stays an `<a href>`
 *
 * A reel is 2.1 GB and August's bundle is 3.8 GB, and `api/client/files.ts` already
 * states the rule for the second: "the browser is the only thing that should hold it".
 * Reading a download through `fetch` to inspect its status first means holding all of
 * it in memory to find out whether it was a 404, and then handing the same bytes back
 * to the browser through an object URL. So the browser streams it, as it did before.
 *
 * ## It opens in a tab of its own, and that is R2-03's third part
 *
 * A same-tab `<a href>` is a **navigation**, so a download that fails takes the portal
 * with it: the browser leaves the SPA and renders whatever came back where the files
 * page used to be. With `Content-Disposition` still on the problem body — which is
 * what session 16 sent — Chrome refused the response outright and put
 * `chrome-error://chromewebdata`, "This site can't be reached", in its place; the user
 * had to press Back to get their portal returned to them. `modules/files/routes.ts`
 * fixes the header, and this fixes what is left: even a perfectly formed
 * `application/problem+json` is not a page anybody wants instead of their files.
 *
 * In a tab of its own both outcomes are right and neither costs the portal:
 *
 *   **on success** the browser closes the tab the moment `Content-Disposition:
 *   attachment` arrives, so nothing visibly happens except a download starting;
 *   **on failure** — a 404 for bytes the disk lost, a 403 for a seat that may not have
 *   this kind of file, a 401 for a session that expired overnight, which is the one
 *   that will actually happen — the throwaway tab holds the problem body, `detail` and
 *   `instance` and all, and the portal is exactly where it was left.
 *
 * The alternatives were weighed and refused. `download` on the anchor keeps the tab
 * too, and is worse: a failure is then completely silent, and "nothing happened" is
 * the hardest support call there is. A `fetch` and an object URL is the memory problem
 * above. A `HEAD` first is two round trips and a race with `files_hold!`.
 *
 * `rel="noopener noreferrer"`: a download target has no business reaching back through
 * `window.opener`, and axe's `blank` rule wants it said out loud.
 */

import type { ReactNode } from 'react'

import { bundleHref, fileDownloadHref } from '../../../api/client/index.js'
import { Button } from '../../../components/portal/Button.js'
import { More } from '../../../components/portal/Text.js'

export type DownloadLinkProps = {
  readonly id: string
  /** The accessible name: "Download Reels — 14 edits", "Open the July report". */
  readonly label: string
  /** The words on screen. "Download" unless the row calls it something else. */
  readonly children?: ReactNode | undefined
}

export function DownloadLink({ id, label, children }: DownloadLinkProps) {
  return (
    <More asChild aria-label={label}>
      <a href={fileDownloadHref(id)} target="_blank" rel="noopener noreferrer">
        {children ?? 'Download'}
      </a>
    </More>
  )
}

/**
 * "Download August" — the whole month as one ZIP, on the same terms.
 *
 * A `Button` rather than a `More` because that is what the topbar draws, and the
 * only other difference from the link above: the bundle is 3.8 GB, which is the
 * clearest case there is for leaving the bytes to the browser.
 */
export function BundleDownloadButton({ period, label }: BundleDownloadButtonProps) {
  return (
    <Button asChild>
      <a href={bundleHref(period)} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    </Button>
  )
}

export type BundleDownloadButtonProps = {
  readonly period: string
  /** "Download Aug 2026" — the caller formats the month. */
  readonly label: string
}
